import { DnsRecordListBase } from "../DnsRecordListBase/index.js";
import type { Ingress, IngressCommon } from "../Ingress/index.js";
import type { DnsZoneCommon } from "../DnsZone/index.js";
import { DnsZone } from "../DnsZone/index.js";
import type { DnsRecordSettingsData } from "../DnsRecordSettings/index.js";
import { provideReact } from "../../react/index.js";
import type { TxtRecordListItem } from "./types.js";

interface ZoneWithUpdate {
  dnsZone: DnsZone;
  update: string[];
}
export class DnsRecordTxtList extends DnsRecordListBase<TxtRecordListItem> {
  public constructor(
    ingress: IngressCommon,
    parentZone: DnsZoneCommon,
    items: TxtRecordListItem[],
  ) {
    super(items, parentZone, ingress);
  }

  private itemsEqual(a: TxtRecordListItem, b: TxtRecordListItem): boolean {
    return a.domain === b.domain && a.entry === b.entry;
  }

  public static getFromIngress = provideReact(
    async (ingress: Ingress, rootZone: DnsZone) => {
      const ingressDetailed = await ingress.getDetailed();
      const dnsZoneDetailed = await rootZone.getDetailed();
      const zones = await DnsZone.query(ingressDetailed.project, {
        domain: ingressDetailed.hostname,
      }).execute();
      const items: TxtRecordListItem[] = [];
      zones.items.forEach((i) =>
        (i.recordSet.txt.entries ?? []).forEach((a) =>
          items.push({ domain: i.domain, entry: a, type: "unchanged" }),
        ),
      );
      return new DnsRecordTxtList(ingressDetailed, dnsZoneDetailed, items);
    },
  );

  public addItem(item: TxtRecordListItem) {
    this.items = [...this.items, { ...item, type: "new" }];
  }
  public deleteItem(item: TxtRecordListItem) {
    if (item.type === "new") {
      this.items = this.items.filter((i) => !this.itemsEqual(item, i));
    }
    this.items = this.items.map((i) =>
      this.itemsEqual(item, i) ? { ...item, type: "deleted" } : i,
    );
  }

  public updateItem(oldItem: TxtRecordListItem, newItem: TxtRecordListItem) {
    if (oldItem.type === "new") {
      this.items = this.items.map((i) =>
        this.itemsEqual(oldItem, i) ? { ...newItem, type: "new" } : i,
      );
      return;
    }
    const index = this.items.findIndex((i) => this.itemsEqual(oldItem, i));
    this.deleteItem(oldItem);
    this.items.splice(index, 0, { ...newItem, type: "new" });
  }

  private resolveTxtRecordUpdate(
    existingItems: string[],
    update: TxtRecordListItem,
  ): string[] {
    if (update.type === "new") {
      return [...existingItems, update.entry];
    }
    if (update.type === "deleted") {
      return existingItems.filter((i) => i !== update.entry);
    }
    return existingItems;
  }

  private async resolveItemUpdates() {
    const zones = await DnsZone.query(this.ingress.project, {
      domain: this.ingress.hostname,
    }).execute();
    const zonesToUpdate: Record<string, ZoneWithUpdate> = {};
    const items = this.getItems(["unchanged"]);

    for (const item of items) {
      const zone = zonesToUpdate[item.domain];
      if (zone === undefined) {
        const zoneFromApi = zones.items.find((i) => i.domain === item.domain);
        if (!zoneFromApi) {
          const newZone = await DnsZone.create(
            this.getPrefix(item.domain),
            this.parentZone.id,
          );
          const resolved = this.resolveTxtRecordUpdate([], item);
          zonesToUpdate[item.domain] = { update: resolved, dnsZone: newZone };
          continue;
        }
        const resolved = this.resolveTxtRecordUpdate(
          zoneFromApi.recordSet.txt.entries ?? [],
          item,
        );
        zonesToUpdate[item.domain] = {
          update: resolved,
          dnsZone: zoneFromApi,
        };
        continue;
      }
      const update = this.resolveTxtRecordUpdate(zone.update, item);
      zonesToUpdate[item.domain] = { update: update, dnsZone: zone.dnsZone };
    }
    return zonesToUpdate;
  }

  public async saveItems(settings: DnsRecordSettingsData): Promise<void> {
    const resolvedItems = await this.resolveItemUpdates();

    const keys = Object.keys(resolvedItems);
    for (const key of keys) {
      const zone = resolvedItems[key];
      if (zone === undefined) {
        continue;
      }
      await zone.dnsZone.setTxtRecord(zone.update, settings);
    }
  }
}
